import React, { useEffect, useState } from 'react'
import Icons from '../assets/icons'
import { twMerge } from 'tailwind-merge'

interface DropdownOption {
    title: string;
    action: () => void;
    icon?: React.ReactNode; 
    [key: string]: any;
}

interface DropdownProps {
    label: string,
    responsive: boolean,
    icon: React.ReactElement,
    options: DropdownOption[],
    classProps?: string,
    containerClassProps?: string,
    onOpen?: () => void,
    isSidebarOpen?: boolean
    canOpen?: boolean
}

const Dropdown: React.FC<DropdownProps> = ({ 
    classProps, 
    containerClassProps, 
    label, 
    icon, 
    options, 
    responsive, 
    onOpen, 
    isSidebarOpen, 
    canOpen = true 
}) => {
    const [open, setOpen] = useState(false)

    const toggleDropdown = () => {
        if (responsive && onOpen) {
            onOpen();
            setOpen(true);
            return;
        }

        const newState = !open
        setOpen(newState)
    }

    useEffect(() => {
        if (!isSidebarOpen) setOpen(false)
    }, [isSidebarOpen])

    const ButtonBaseStyle = 'duration-200 flex items-center justify-between font-medium w-full p-4 cursor-pointer min-h-[60px] transition-colors';
    const ButtonColors = 'text-neutro-700 fill-neutro-700 hover:bg-gray-100'; 
    const ButtonFinalStyle = twMerge(ButtonColors, ButtonBaseStyle);

    const DropContainerStyle = twMerge(
        'duration-300 overflow-hidden transition-all flex flex-col border-b border-neutro-200 bg-gray-50 gap-1', 
        open && canOpen ? "max-h-[1200px] opacity-100 py-2" : "max-h-0 opacity-0 py-0"
    );

    const OptionBaseStyle = 'duration-200 w-full text-left py-5 pr-4 pl-14 text-sm font-medium cursor-pointer border-l-4 border-transparent flex items-center gap-3';
    
    const OptionColors = 'text-gray-600 fill-gray-500 hover:text-primario-700 hover:fill-primario-700 hover:bg-primario-50 hover:border-primario-700';
    const OptionFinalStyle = twMerge(OptionBaseStyle, OptionColors);

    const ActionContainerStyle = twMerge('border-y border-neutro-200 text-neutro-700', classProps)

    return (
        <div className={twMerge("flex flex-col w-full", containerClassProps)}>
            <div className={ActionContainerStyle}>
                <button onClick={toggleDropdown} className={ButtonFinalStyle} title={responsive ? label : ''}>
                    <div className={`flex items-center gap-3 overflow-hidden ${responsive ? 'justify-center w-full' : ''}`}>
                        <span className="shrink-0 flex items-center justify-center">
                            {icon}
                        </span>
                        
                        <p className={`whitespace-nowrap transition-all duration-300 ${responsive ? 'opacity-0 w-0' : 'opacity-100 w-auto'}`}>
                            {label}
                        </p>
                    </div>
                    
                    {!responsive && (
                        <div className={`transition-transform duration-300 ${open ? 'rotate-180' : ''}`}>
                             <Icons.ArrowDown pathStyle='fill-gray-400' width={12} height={12}/>
                        </div>
                    )}
                </button>
            </div>

            <div className={DropContainerStyle}>
                {options.map((item, index) => (
                    <button 
                        key={index} 
                        onClick={() => item.action?.()} 
                        className={OptionFinalStyle} 
                    >
                        {item.icon && (
                            <span className="shrink-0 flex items-center">
                                {item.icon}
                            </span>
                        )}
                        {item.title}
                    </button>
                ))}
            </div>
        </div>
    )
}

export default Dropdown;